import { useEffect, useState } from "react";
import {
  EuiFlexGroup,
  EuiFlexItem,
  EuiPanel,
  EuiStat,
  EuiText,
  EuiTextColor,
  EuiSpacer,
  EuiLoadingSpinner,
} from "@elastic/eui";
import BauhausService from "../services/bauhaus-service";

const BauhausInAdHoc = ({ startDate, endDate }) => {
  const [bauhausData, setBauhausData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const token = sessionStorage.getItem("krakenT");
        const response = await BauhausService.global_stats({
          startDate,
          endDate,
          token,
        });

        const data = response?.data?.data;
        setBauhausData(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [startDate, endDate]);

  if (error) {
    return <div>Error: {error}</div>;
  }

  return (
    <div>
      <EuiFlexGroup wrap>
        {/* Métricas principales */}
        {[
          {
            title: "Attended Calls",
            value: `${Math.round(bauhausData?.attended_calls_percentage)}%`,
            description: bauhausData?.attended_calls,
            color: "success",
          },
          {
            title: "Non-Attended Calls",
            value: `${Math.round(bauhausData?.non_attended_calls_percentage)}%`,
            description: bauhausData?.non_attended_calls,
            color: "danger",
          },
          {
            title: "Service Level",
            value: `${Math.round(bauhausData?.service_level)}%`,
            description: "Before 45 seconds",
            color: "primary",
          },
        ].map((metric, index) => (
          <EuiFlexItem key={index} grow={1}>
            <EuiPanel>
              {loading ? (
                <EuiLoadingSpinner size="m" />
              ) : (
                <>
                  {" "}
                  <EuiStat
                    title={
                      <span style={{ fontSize: "1.4em" }}>{metric.value}</span>
                    }
                    textAlign="left"
                    isLoading={loading}
                    titleColor={metric.color}
                    description={
                      <EuiTextColor color={metric.color}>
                        <span style={{ fontSize: "1em" }}>
                          {metric.description}
                        </span>
                      </EuiTextColor>
                    }
                  />
                  <EuiText size="s" textAlign="left">
                    {metric.title}
                  </EuiText>
                </>
              )}
            </EuiPanel>
          </EuiFlexItem>
        ))}

        {/* Transferencias (Intentadas y Completadas en un solo panel) */}
        <EuiFlexItem grow={1}>
          <EuiPanel>
            <EuiFlexGroup direction="column" gutterSize="s">
              <EuiFlexItem>
                <EuiText size="xs" textAlign="center">
                  Attempted Transfers
                </EuiText>
                <EuiText
                  style={{ fontSize: 25 }}
                  textAlign="center"
                  color="warning"
                >
                  <strong>
                    {Math.round(bauhausData.percentage_attempted_transfers)}%
                  </strong>{" "}
                  ({bauhausData.attempted_transfers})
                </EuiText>
              </EuiFlexItem>
              <EuiFlexItem>
                <EuiText size="xs" textAlign="center">
                  Completed Transfers
                </EuiText>
                <EuiText
                  style={{ fontSize: 25 }}
                  textAlign="center"
                  color="success"
                >
                  <strong>
                    {Math.round(bauhausData.percentage_completed_transfers)}%
                  </strong>{" "}
                  ({bauhausData.completed_transfers})
                </EuiText>
              </EuiFlexItem>
            </EuiFlexGroup>
          </EuiPanel>
        </EuiFlexItem>

        {/* AHT y Ringing Time */}
        <EuiFlexItem grow={2}>
          <EuiPanel>
            <EuiFlexGroup>
              <EuiFlexItem>
                <EuiStat
                  title={
                    <span style={{ fontSize: "1.4em" }}>
                      {bauhausData?.talk_time}
                    </span>
                  }
                  textAlign="left"
                  isLoading={loading}
                  titleColor="accent"
                  description={<span style={{ fontSize: "1em" }}>AHT</span>}
                >
                  <EuiTextColor color="accent" style={{ fontSize: "1em" }}>
                    Average Handle Time
                  </EuiTextColor>
                </EuiStat>
              </EuiFlexItem>
              <EuiFlexItem>
                <EuiStat
                  title={
                    <span style={{ fontSize: "1.4em" }}>
                      {bauhausData?.hold_time}
                    </span>
                  }
                  description={<span style={{ fontSize: "1em" }}>HT</span>}
                  textAlign="left"
                  titleColor="accent"
                  isLoading={loading}
                >
                  <EuiTextColor color="accent" style={{ fontSize: "1em" }}>
                    Ringing Time
                  </EuiTextColor>
                </EuiStat>
              </EuiFlexItem>
            </EuiFlexGroup>
          </EuiPanel>
        </EuiFlexItem>

        {/* Encuestas */}
        <EuiFlexItem grow={1}>
          <EuiPanel>
            <EuiStat
              title={
                <EuiTextColor color="accent">
                  <span style={{ fontSize: "1.4em" }}>
                    {bauhausData.survey_listened}%
                  </span>
                </EuiTextColor>
              }
              textAlign="left"
              isLoading={loading}
              titleColor="accent"
              description={
                <span style={{ fontSize: "1em" }}>Survey listened</span>
              }
            >
              <div
                style={{
                  display: "flex",
                  fontSize: "1em",
                }}
              >
                <EuiTextColor>
                  {bauhausData.survey_calls}% scored |
                </EuiTextColor>
                <EuiTextColor color="accent">
                  &nbsp;{bauhausData.survey_avg} AVG
                </EuiTextColor>
              </div>
            </EuiStat>
          </EuiPanel>
        </EuiFlexItem>
      </EuiFlexGroup>
    </div>
  );
};

// Función auxiliar para formatear tiempos en hh:mm
const formatTime = (timeInSeconds) => {
  if (!timeInSeconds) return "00:00";
  const hours = Math.floor(timeInSeconds / 3600);
  const minutes = Math.floor((timeInSeconds % 3600) / 60);
  return `${hours.toString().padStart(2, "0")}:${minutes
    .toString()
    .padStart(2, "0")}`;
};

export default BauhausInAdHoc;
